<template>
  <AdminLayout>
    <template #topbar-search>
      <div class="flex flex-column ">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText v-model="searchValue" placeholder="Search by keywords..." size="small" />
        </IconField>
      </div>
    </template>
    <template #header>
      <Breadcrumb :items="items" />
      <Button label="Export" class="accent-button" @click="ExportStudents" />
    </template>
    <div v-if="currentView !== 'List'" class="back-section">
      <Button icon="pi pi-arrow-left" label="Back" class="p-button-text Caption back-button mb-2" @click="goBack" />
    </div>
    <div class="container">
      <div v-if="currentView === 'List'" class="flex justify-content-end mb-4 md:px-4">
        <div class="flex align-items-center gap-2">
          <label class="Label text-center">Sort By:</label>
          <Select v-model="selectedSort" :options="sortOptions" optionLabel="label" optionValue="value" placeholder=""
            class="w-36" />
        </div>
      </div>
      <StudentList v-if="currentView === 'List'" :tableData="tableData" :isLoading="isLoading"
        :first="(currentPage - 1) * rowsPerPage" :rowsPerPage="rowsPerPage" :totalRecords="totalRecords"
        :currentPage="currentPage" @page-change="onPageChange" @view-profile="viewProfile"
        @status-change="onStatusChange" @delete-student="showDeleteConfirmationDialog"
        @details-view="viewDetails" />
      <studentPublicProfile v-if="currentView === 'View profile'" :profileDetails="profileDetails"
        :enrolledTutors="enrolledTutors" :previousEnrolledTutors="previousEnrolledTutors" />
      <studentDetails v-if="currentView === 'Details'" :studentId="selectedStudentId" />
      <DeleteConfirmationDialog v-model:visible="showDeleteConfirmation" :selectedCount="selectedStudents.length"
        :loading="deleteLoading" @confirm="handleDeleteConfirm" />
      <Toast />
    </div>
  </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import AdminLayout from "@/layout/AdminLayout.vue";
import studentPublicProfile from "@/components/dashboard/admin/students/studentPublicProfile.vue";
import StudentList from "@/components/dashboard/admin/students/studentList.vue";
import studentDetails from "@/components/dashboard/admin/students/studentDetails.vue";
import Breadcrumb from "@/components/CustomBreadcrumb.vue"
import Button from 'primevue/button';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import Toast from 'primevue/toast';
import Select from 'primevue/select';
import { useToast } from 'primevue/usetoast';
import DeleteConfirmationDialog from '@/components/dashboard/admin/confirmationDialog.vue';
import { notifySuccess } from '@/utils/notification';
import { getStudents, updateStatus, getStudentProfile, getEnrolledTutors, getPreviousTutors, exportStudents, deleteStudents } from "@/api/admin";

const currentView = ref("List");
const profileDetails = ref(null);
const enrolledTutors = ref(null);
const previousEnrolledTutors = ref(null);
const selectedStudentId = ref(null);
const tableData = ref([]);
const isLoading = ref(false);
const totalRecords = ref(0);
const rowsPerPage = ref(10);
const currentPage = ref(1);
const searchValue = ref("");
const showDeleteConfirmation = ref(false);
const deleteLoading = ref(false);
const selectedStudents = ref([]);
const toast = useToast();
const selectedSort = ref("newest");
const sortOptions = ref([
  { label: 'Newest', value: 'newest' },
  { label: 'Oldest', value: 'oldest' },
  { label: 'Name: A-Z', value: 'a-z' },
  { label: 'Name: Z-A', value: 'z-a' },
]);

const fetchStudents = async () => {
  try {
    isLoading.value = true;
    const response = await getStudents({
      page: currentPage.value,
      limit: rowsPerPage.value,
      searchTerm: searchValue.value,
      sortBy: selectedSort.value
    });

    tableData.value = response.data.data.students.map(student => ({
      ...student,
      isActive: student.isActive ?? true
    }));
    totalRecords.value = response.data.data.pagination.total;
  } catch (error) {
    console.error("Failed to fetch students:", error);
  } finally {
    isLoading.value = false;
  }
};

const items = computed(() => {
  const breadcrumbItems = [{ label: "Students" }];
  if (currentView.value === "View profile") {
    breadcrumbItems.push({ label: profileDetails.value.username });
  } else if (currentView.value === "Details") {
    breadcrumbItems.push({ label: "Details" });
  }
  return breadcrumbItems;
});

watch(searchValue, () => {
  currentPage.value = 1;
  fetchStudents();
});

watch(selectedSort, () => {
  currentPage.value = 1
  fetchStudents();
});

onMounted(fetchStudents);

const onStatusChange = async (rowData) => {
  try {
    await updateStatus(rowData._id);
    fetchStudents();
  } catch (err) {
    console.error('Failed to update status', err);
  }
};

const viewProfile = async (rowData) => {
  try {
    const [profile, tutors, previosTutors] = await Promise.allSettled([
      getStudentProfile(rowData._id),
      getEnrolledTutors(rowData._id),
      getPreviousTutors(rowData._id)
    ]);
    profileDetails.value = profile.value.data.data ?? {};
    enrolledTutors.value = tutors.value.data.data ?? [];
    previousEnrolledTutors.value = previosTutors.value.data.data ?? [];

  } catch (err) {
    console.error("Unexpected error:", err);
  }
  currentView.value = "View profile";
};

const viewDetails = (rowData) => {
  selectedStudentId.value = rowData._id;
  currentView.value = "Details";
};

const ExportStudents = async () => {
  try {
    // const res = await exportStudents({ searchTerm: searchValue.value });
    const res = await exportStudents({ searchTerm: searchValue.value, sortBy: selectedSort.value });
    const filePath = res.data.data.downloadLink;
    const link = document.createElement("a");
    link.href = filePath;
    link.download = "students.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    notifySuccess("Success!", "The file has been downloaded.");
  } catch (err) {
    console.error("Failed to export", err);
  }
}

const showDeleteConfirmationDialog = (studentIDs) => {
  selectedStudents.value = studentIDs;
  showDeleteConfirmation.value = true;
};

const handleDeleteConfirm = async () => {
  deleteLoading.value = true;
  try {
    await deleteStudents({ studentIds: selectedStudents.value });
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Successfully deleted ${selectedStudents.value.length} student(s)`,
      life: 3000
    });
    await fetchStudents();
    showDeleteConfirmation.value = false;
    selectedStudents.value = [];
  } catch (err) {
    console.error("Failed to delete students", err);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete student(s)',
      life: 3000
    });
  } finally {
    deleteLoading.value = false;
  }
};
const onPageChange = ({ page, limit }) => {
  currentPage.value = page;
  rowsPerPage.value = limit;
  fetchStudents();
};

const showList = () => { currentView.value = 'List'; };
const goBack = () => { showList(); };
</script>

<style>
.container {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.back-section {
  display: flex;
  align-items: center;
}

.back-button {
  color: var(--gray-600) !important;
  font-size: 12px !important;
}

.back-button:hover {
  color: var(--gray-600) !important;
  background-color: var(--gray-200) !important;
}

.back-button:active {
  color: var(--gray-600) !important;
}

.pi-arrow-left {
  color: var(--gray-600) !important;
  font-size: 12px !important;
}
</style>
